import { useMemo, useState } from "react";
import { Layout } from "@/components/layout";
import {
  useGetMe,
  useGetSkyboxSummary,
  useListSkyboxEvents,
  useListSkyboxExposure,
  useListSkyboxEventCandidates,
  useMapSkyboxEvent,
  useSyncSkybox,
  useUpdateSkyboxAlertConfig,
  useListSlackChannels,
  useGetSkyboxAlertPullerPreview,
  useSendSkyboxAlertPuller,
  useSendSkyboxAlertAll,
  getGetSkyboxAlertPullerPreviewQueryKey,
  getGetSkyboxSummaryQueryKey,
  getListSkyboxEventsQueryKey,
  getListSkyboxExposureQueryKey,
  getListSkyboxEventCandidatesQueryKey,
  getListSlackChannelsQueryKey,
  type ExposureFlag,
  type SkyboxEventMapping,
  type SkyboxSummary,
  type ListSkyboxExposureParams,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Bell,
  Check,
  ChevronsUpDown,
  ExternalLink,
  Link2,
  Link2Off,
  Loader2,
  Lock,
  RefreshCw,
  Search,
  Ticket,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const SEVERITY_STYLE: Record<string, string> = {
  critical: "bg-red-500/10 text-red-400 border-red-500/40",
  high: "bg-orange-500/10 text-orange-400 border-orange-500/40",
  warning: "bg-amber-500/10 text-amber-400 border-amber-500/40",
};

const EXPOSURE_LABEL: Record<string, string> = {
  double_listed: "Double listed",
  listed_vs_sold: "Listed vs sold",
  double_sold: "Double sold",
};

function fmtDate(s?: string | null): string {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function fmtRelative(s?: string | null): string {
  if (!s) return "Never";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "Never";
  const diff = Date.now() - d.getTime();
  const min = Math.round(diff / 60_000);
  if (min < 1) return "Just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  return `${day}d ago`;
}

function seatRange(from?: string | null, to?: string | null): string {
  if (from && to) return from === to ? from : `${from}–${to}`;
  return from || to || "—";
}

export default function Skybox() {
  const { data: me } = useGetMe();

  if (me && me.role !== "admin" && me.role !== "pricing") {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full w-full">
          <p className="text-muted-foreground">
            Skybox is restricted to administrators and the pricing team.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SkyboxContent />
    </Layout>
  );
}

function SkyboxContent() {
  const queryClient = useQueryClient();
  const { data: summary, isLoading } = useGetSkyboxSummary();

  const syncMutation = useSyncSkybox({
    mutation: {
      onSuccess: (res) => {
        queryClient.invalidateQueries({ queryKey: getGetSkyboxSummaryQueryKey() });
        queryClient.invalidateQueries({ queryKey: getListSkyboxEventsQueryKey() });
        queryClient.invalidateQueries({
          queryKey: getListSkyboxExposureQueryKey(),
        });
        toast.success(
          `Sync complete · ${res.flagsOpen} open flag${res.flagsOpen === 1 ? "" : "s"}` +
            (res.alertsSent > 0 ? ` · ${res.alertsSent} alert sent` : ""),
        );
      },
      onError: () => toast.error("Skybox sync failed"),
    },
  });

  return (
    <div className="flex flex-col h-full w-full bg-background p-2 sm:p-4 overflow-hidden">
      <div className="mb-4 shrink-0 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-medium text-foreground">Skybox</h1>
          <p className="text-xs text-muted-foreground">
            Cross-reference VividSeats (Skybox) inventory against FVE to catch
            double exposure
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-muted-foreground">
            Last synced {fmtRelative(summary?.lastSyncedAt)}
          </span>
          <Button
            size="sm"
            className="h-8 gap-1.5"
            disabled={!summary?.configured || syncMutation.isPending}
            onClick={() => syncMutation.mutate()}
          >
            {syncMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            Sync now
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : !summary?.configured ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="max-w-md text-center space-y-2">
            <p className="text-sm text-foreground">Skybox is not configured</p>
            <p className="text-xs text-muted-foreground">
              Set the Skybox API credentials to enable inventory and sales
              cross-referencing.
            </p>
          </div>
        </div>
      ) : (
        <>
          <SummaryStrip summary={summary} />
          <div className="flex-1 overflow-hidden flex flex-col bg-card border border-border rounded-md">
            <Tabs
              defaultValue="exposure"
              className="flex-1 min-h-0 flex flex-col w-full"
            >
              <div className="border-b border-border bg-muted p-2 shrink-0">
                <TabsList className="bg-transparent justify-start h-auto flex-wrap gap-2 p-0">
                  <TabsTrigger
                    value="exposure"
                    className="text-xs h-8 px-4 data-[state=active]:bg-secondary border border-transparent data-[state=active]:border-border rounded"
                  >
                    Exposure
                  </TabsTrigger>
                  <TabsTrigger
                    value="mappings"
                    className="text-xs h-8 px-4 data-[state=active]:bg-secondary border border-transparent data-[state=active]:border-border rounded"
                  >
                    Event Mappings
                  </TabsTrigger>
                  <TabsTrigger
                    value="alerts"
                    className="text-xs h-8 px-4 data-[state=active]:bg-secondary border border-transparent data-[state=active]:border-border rounded"
                  >
                    Alerts
                  </TabsTrigger>
                </TabsList>
              </div>
              <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
                <TabsContent value="exposure" className="m-0 flex-1 min-h-0">
                  <ExposureTab />
                </TabsContent>
                <TabsContent value="mappings" className="m-0 flex-1 min-h-0">
                  <MappingsTab />
                </TabsContent>
                <TabsContent value="alerts" className="m-0 flex-1 min-h-0">
                  <AlertsTab
                    alertConfigured={summary.alertConfigured}
                    alertEnabled={summary.alertEnabled}
                    alertChannel={summary.alertChannel ?? null}
                  />
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | number;
  tone?: string;
}) {
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={cn("text-lg font-semibold tabular-nums", tone)}>
        {value}
      </div>
    </div>
  );
}

function SummaryStrip({ summary }: { summary: SkyboxSummary }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-4 shrink-0">
      <StatCard label="Events mapped" value={summary.mappedEvents} />
      <StatCard
        label="Unmapped"
        value={summary.unmappedEvents}
        tone={summary.unmappedEvents > 0 ? "text-amber-400" : undefined}
      />
      <StatCard label="Active tracked" value={summary.totalActive} />
      <StatCard label="Sold tracked" value={summary.totalSold} />
      <StatCard
        label="Critical"
        value={summary.criticalFlags}
        tone={summary.criticalFlags > 0 ? "text-red-400" : "text-muted-foreground"}
      />
      <StatCard
        label="High"
        value={summary.highFlags}
        tone={summary.highFlags > 0 ? "text-orange-400" : "text-muted-foreground"}
      />
      <StatCard
        label="Warning"
        value={summary.warningFlags}
        tone={summary.warningFlags > 0 ? "text-amber-400" : "text-muted-foreground"}
      />
    </div>
  );
}

function ExposureTab() {
  const [status, setStatus] = useState<string>("open");
  const [severity, setSeverity] = useState<string>("all");
  const [exposureType, setExposureType] = useState<string>("all");
  const [eventId, setEventId] = useState<string>("all");
  const [q, setQ] = useState("");

  const { data: events } = useListSkyboxEvents();
  const eventOptions = useMemo(
    () => (events ?? []).filter((e) => e.mapped),
    [events],
  );

  const params: ListSkyboxExposureParams = {};
  if (status !== "open") params.status = status as ListSkyboxExposureParams["status"];
  else params.status = "open";
  if (severity !== "all")
    params.severity = severity as ListSkyboxExposureParams["severity"];
  if (exposureType !== "all")
    params.exposureType =
      exposureType as ListSkyboxExposureParams["exposureType"];
  if (eventId !== "all") params.fveEventId = Number(eventId);
  if (q.trim()) params.q = q.trim();

  const { data: flags, isLoading, isFetching } = useListSkyboxExposure(params);

  const hasFilters =
    status !== "open" ||
    severity !== "all" ||
    exposureType !== "all" ||
    eventId !== "all" ||
    q.trim().length > 0;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex flex-wrap items-center gap-2 p-3 border-b border-border shrink-0">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search section, seat, listing, email, puller…"
            className="h-8 w-64 pl-7 text-xs"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-8 w-28 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="open" className="text-xs">Open</SelectItem>
            <SelectItem value="resolved" className="text-xs">Resolved</SelectItem>
            <SelectItem value="all" className="text-xs">All</SelectItem>
          </SelectContent>
        </Select>
        <Select value={severity} onValueChange={setSeverity}>
          <SelectTrigger className="h-8 w-32 text-xs">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All severities</SelectItem>
            <SelectItem value="critical" className="text-xs">Critical</SelectItem>
            <SelectItem value="high" className="text-xs">High</SelectItem>
            <SelectItem value="warning" className="text-xs">Warning</SelectItem>
          </SelectContent>
        </Select>
        <Select value={exposureType} onValueChange={setExposureType}>
          <SelectTrigger className="h-8 w-36 text-xs">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All types</SelectItem>
            <SelectItem value="double_listed" className="text-xs">Double listed</SelectItem>
            <SelectItem value="listed_vs_sold" className="text-xs">Listed vs sold</SelectItem>
            <SelectItem value="double_sold" className="text-xs">Double sold</SelectItem>
          </SelectContent>
        </Select>
        <Select value={eventId} onValueChange={setEventId}>
          <SelectTrigger className="h-8 w-44 text-xs">
            <SelectValue placeholder="Event" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All events</SelectItem>
            {eventOptions.map((e) => (
              <SelectItem
                key={e.fveEventId}
                value={String(e.fveEventId)}
                className="text-xs"
              >
                {e.fveLabel}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1 text-xs text-muted-foreground"
            onClick={() => {
              setStatus("open");
              setSeverity("all");
              setExposureType("all");
              setEventId("all");
              setQ("");
            }}
          >
            <X className="h-3.5 w-3.5" />
            Clear
          </Button>
        )}
        {isFetching && (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
        )}
        <div className="ml-auto">
          <AlertAllDialog />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto custom-scrollbar">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : !flags || flags.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-sm text-foreground">No exposure flags</p>
            <p className="text-xs text-muted-foreground">
              {status === "open"
                ? "No open double-exposure flags for the current filters."
                : "No flags match the current filters."}
            </p>
          </div>
        ) : (
          <TooltipProvider delayDuration={200}>
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted z-10">
                <tr className="text-left text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Severity</th>
                  <th className="px-3 py-2 font-medium">Event</th>
                  <th className="px-3 py-2 font-medium">Puller</th>
                  <th className="px-3 py-2 font-medium">Type</th>
                  <th className="px-3 py-2 font-medium">Location</th>
                  <th className="px-3 py-2 font-medium">FVE side</th>
                  <th className="px-3 py-2 font-medium">Reach side</th>
                  <th className="px-3 py-2 font-medium">Skybox side</th>
                  <th className="px-3 py-2 font-medium">Overlap</th>
                  <th className="px-3 py-2 font-medium">Detected</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {flags.map((f) => (
                  <ExposureRow key={f.id} flag={f} />
                ))}
              </tbody>
            </table>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
}

const STATE_STYLE: Record<string, string> = {
  listed: "bg-sky-500/10 text-sky-400 border-sky-500/30",
  sold: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
};

function StateBadge({ state }: { state: string }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "text-[10px] capitalize",
        STATE_STYLE[state] ?? "bg-muted text-muted-foreground border-border",
      )}
    >
      {state}
    </Badge>
  );
}

function ExposureRow({ flag: f }: { flag: ExposureFlag }) {
  const stillListed = f.fveState === "listed";
  return (
    <tr className="border-t border-border hover:bg-muted/40 align-top">
      <td className="px-3 py-2">
        <Badge
          variant="outline"
          className={cn(
            "text-[10px] capitalize",
            SEVERITY_STYLE[f.severity] ?? "",
          )}
        >
          {f.severity}
        </Badge>
        {f.status === "resolved" && (
          <div className="mt-1 text-[10px] text-muted-foreground">resolved</div>
        )}
      </td>
      <td className="px-3 py-2 text-foreground">{f.fveEventLabel ?? "—"}</td>
      <td className="px-3 py-2">
        {f.puller ? (
          <span className="text-foreground">{f.puller}</span>
        ) : (
          <span className="text-muted-foreground">Unassigned</span>
        )}
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {EXPOSURE_LABEL[f.exposureType] ?? f.exposureType}
      </td>
      <td className="px-3 py-2 text-foreground">
        <div>{f.section ?? f.skyboxSection ?? "—"}</div>
        <div className="text-[10px] text-muted-foreground">
          Row {f.row ?? f.skyboxRow ?? "—"}
        </div>
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1.5">
          <StateBadge state={f.fveState} />
          {f.fveHoldActive && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Lock className="h-3 w-3 text-amber-400" />
              </TooltipTrigger>
              <TooltipContent>On hold in FVE</TooltipContent>
            </Tooltip>
          )}
          {f.reachListingUrl && (
            <Tooltip>
              <TooltipTrigger asChild>
                <a
                  href={f.reachListingUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-muted-foreground hover:text-primary"
                >
                  <ExternalLink className="h-3 w-3" />
                </a>
              </TooltipTrigger>
              <TooltipContent>Open in Reach</TooltipContent>
            </Tooltip>
          )}
        </div>
        <div className="mt-0.5 text-foreground">
          {seatRange(f.fveSeatFrom, f.fveSeatTo)}
        </div>
        {f.fveVendorAccountEmail && (
          <div className="text-[10px] text-muted-foreground truncate max-w-[160px]">
            {f.fveVendorAccountEmail}
          </div>
        )}
      </td>
      <td className="px-3 py-2">
        {f.reachSaleUrl ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={f.reachSaleUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-emerald-400 hover:underline"
              >
                <Ticket className="h-3 w-3" />
                {f.reachSaleId ? `#${f.reachSaleId}` : "Sold"}
              </a>
            </TooltipTrigger>
            <TooltipContent>Matched Reach sale</TooltipContent>
          </Tooltip>
        ) : f.reachListingUrl ? (
          <a
            href={f.reachListingUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-primary"
          >
            <ExternalLink className="h-3 w-3" />
            Listing
          </a>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1.5">
          <StateBadge state={f.skyboxState} />
          {f.skyboxUrl && (
            <Tooltip>
              <TooltipTrigger asChild>
                <a
                  href={f.skyboxUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-muted-foreground hover:text-primary"
                >
                  <ExternalLink className="h-3 w-3" />
                </a>
              </TooltipTrigger>
              <TooltipContent>Open in Skybox</TooltipContent>
            </Tooltip>
          )}
        </div>
        <div className="mt-0.5 text-foreground">
          {seatRange(f.skyboxLowSeat, f.skyboxHighSeat)}
        </div>
        <div className="text-[10px] text-muted-foreground">
          {f.skyboxQuantity != null ? `qty ${f.skyboxQuantity}` : ""}
          {f.skyboxQuantity != null && f.skyboxInvoiceId ? " · " : ""}
          {f.skyboxInvoiceId ? `inv ${f.skyboxInvoiceId}` : ""}
        </div>
      </td>
      <td className="px-3 py-2 text-foreground">
        {f.overlapSeats
          ? f.overlapSeats
          : f.overlapQuantity != null
            ? `${f.overlapQuantity} seat${f.overlapQuantity === 1 ? "" : "s"}`
            : "—"}
      </td>
      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
        {fmtRelative(f.detectedAt)}
      </td>
      <td className="px-3 py-2 text-right">
        {stillListed && f.status !== "resolved" && (
          <AlertPullerDialog flag={f} />
        )}
      </td>
    </tr>
  );
}

const ALERT_ERROR_LABEL: Record<string, string> = {
  slack_not_configured: "Slack is not configured.",
  no_destination: "No Slack destination for this puller.",
  flag_not_found: "Exposure flag no longer exists.",
  send_failed: "Slack rejected the message.",
};

function AlertPullerDialog({ flag }: { flag: ExposureFlag }) {
  const [open, setOpen] = useState(false);
  const { data: preview, isLoading } = useGetSkyboxAlertPullerPreview(flag.id, {
    query: {
      queryKey: getGetSkyboxAlertPullerPreviewQueryKey(flag.id),
      enabled: open,
    },
  });
  const send = useSendSkyboxAlertPuller();

  const onSend = () => {
    send.mutate(
      { id: flag.id },
      {
        onSuccess: (res) => {
          if (res.ok) {
            toast.success(
              preview?.pullerName
                ? `Alert sent to ${preview.pullerName}`
                : "Alert sent on Slack",
            );
            setOpen(false);
          } else {
            toast.error(
              ALERT_ERROR_LABEL[res.error ?? ""] ?? "Could not send alert.",
            );
          }
        },
        onError: () => toast.error("Could not send alert."),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 gap-1 text-[10px] rounded-full">
          <Bell className="h-3 w-3 text-amber-400" />
          Alert puller
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Alert puller on Slack</DialogTitle>
          <DialogDescription>
            {preview?.pullerName
              ? `This will message ${preview.pullerName} about the double exposure.`
              : "This will message the puller (or fallback channel) about the double exposure."}
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md border border-border bg-muted/40 p-3">
          {isLoading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Building message…
            </div>
          ) : (
            <pre className="whitespace-pre-wrap break-words text-xs text-foreground font-sans">
              {preview?.message ?? "—"}
            </pre>
          )}
        </div>
        {preview && !preview.slackReady && (
          <p className="text-xs text-amber-400">
            Slack isn't configured or no destination is set for this puller — the
            alert can't be delivered.
          </p>
        )}
        <DialogFooter>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpen(false)}
            disabled={send.isPending}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="gap-1"
            onClick={onSend}
            disabled={send.isPending || isLoading || !preview?.slackReady}
          >
            {send.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Bell className="h-3.5 w-3.5" />
            )}
            Send alert
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AlertAllDialog() {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const send = useSendSkyboxAlertAll();

  const onSend = () => {
    send.mutate(undefined, {
      onSuccess: (res) => {
        queryClient.invalidateQueries({
          queryKey: getListSkyboxExposureQueryKey(),
        });
        queryClient.invalidateQueries({ queryKey: getGetSkyboxSummaryQueryKey() });
        queryClient.invalidateQueries({ queryKey: getListSkyboxEventsQueryKey() });
        if (!res.ok) {
          toast.error(
            res.error === "slack_not_configured"
              ? "Slack is not configured."
              : "Could not send alerts.",
          );
          return;
        }
        if (res.totalListed === 0) {
          toast.success("No currently-listed exposures to alert.");
        } else {
          const parts = [`${res.sent} alert${res.sent === 1 ? "" : "s"} sent`];
          if (res.skipped > 0) parts.push(`${res.skipped} skipped (no destination)`);
          if (res.failed > 0) parts.push(`${res.failed} failed`);
          toast.success(parts.join(" · "));
        }
        setOpen(false);
      },
      onError: () => toast.error("Could not send alerts."),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs rounded-full">
          <Bell className="h-3.5 w-3.5 text-amber-400" />
          Alert all listed
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Alert all pullers</DialogTitle>
          <DialogDescription>
            This refreshes Skybox data first, then sends a Slack alert to every
            puller who has an exposure that is still listed. Sold or delisted
            exposures are skipped.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpen(false)}
            disabled={send.isPending}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="gap-1"
            onClick={onSend}
            disabled={send.isPending}
          >
            {send.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Bell className="h-3.5 w-3.5" />
            )}
            {send.isPending ? "Refreshing & sending…" : "Refresh & send alerts"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MappingsTab() {
  const { data: events, isLoading } = useListSkyboxEvents();
  const [onlyUnmapped, setOnlyUnmapped] = useState(false);

  const rows = useMemo(() => {
    const list = events ?? [];
    return onlyUnmapped ? list.filter((e) => !e.mapped) : list;
  }, [events, onlyUnmapped]);

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between gap-2 p-3 border-b border-border shrink-0">
        <p className="text-xs text-muted-foreground">
          Map each FVE event to its Skybox counterpart to enable
          cross-referencing.
        </p>
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
          <Switch checked={onlyUnmapped} onCheckedChange={setOnlyUnmapped} />
          Unmapped only
        </label>
      </div>
      <div className="flex-1 min-h-0 overflow-auto custom-scrollbar">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : rows.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <p className="text-xs text-muted-foreground">
              {onlyUnmapped ? "All events are mapped." : "No enabled events."}
            </p>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-muted z-10">
              <tr className="text-left text-muted-foreground">
                <th className="px-3 py-2 font-medium">FVE event</th>
                <th className="px-3 py-2 font-medium">Skybox event</th>
                <th className="px-3 py-2 font-medium">Match</th>
                <th className="px-3 py-2 font-medium">Active</th>
                <th className="px-3 py-2 font-medium">Sold</th>
                <th className="px-3 py-2 font-medium">Flags</th>
                <th className="px-3 py-2 font-medium">Synced</th>
                <th className="px-3 py-2 font-medium text-right">Mapping</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <MappingRow key={e.fveEventId} row={e} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function MappingRow({ row }: { row: SkyboxEventMapping }) {
  return (
    <tr className="border-t border-border hover:bg-muted/40 align-top">
      <td className="px-3 py-2">
        <div className="text-foreground">{row.fveLabel}</div>
        <div className="text-[10px] text-muted-foreground">
          {fmtDate(row.fveEventDate)}
          {row.fveVenue ? ` · ${row.fveVenue}` : ""}
        </div>
      </td>
      <td className="px-3 py-2">
        {row.mapped ? (
          <>
            <div className="text-foreground">{row.skyboxName ?? "—"}</div>
            <div className="text-[10px] text-muted-foreground">
              {fmtDate(row.skyboxDate)}
              {row.skyboxVenue ? ` · ${row.skyboxVenue}` : ""}
            </div>
          </>
        ) : (
          <span className="text-muted-foreground">Not mapped</span>
        )}
      </td>
      <td className="px-3 py-2">
        {row.mapped ? (
          <span className="text-[10px] text-muted-foreground">
            {row.matchMethod === "manual" ? "Manual" : "Auto"}
            {row.matchConfidence ? ` · ${row.matchConfidence}` : ""}
          </span>
        ) : (
          "—"
        )}
      </td>
      <td className="px-3 py-2 tabular-nums text-foreground">
        {row.mapped ? row.activeCount : "—"}
      </td>
      <td className="px-3 py-2 tabular-nums text-foreground">
        {row.mapped ? row.soldCount : "—"}
      </td>
      <td className="px-3 py-2">
        {row.openFlags > 0 ? (
          <div className="flex items-center gap-1">
            {row.criticalFlags > 0 && (
              <span className="text-[10px] font-semibold text-red-400">
                {row.criticalFlags}C
              </span>
            )}
            {row.highFlags > 0 && (
              <span className="text-[10px] font-semibold text-orange-400">
                {row.highFlags}H
              </span>
            )}
            {row.warningFlags > 0 && (
              <span className="text-[10px] font-semibold text-amber-400">
                {row.warningFlags}W
              </span>
            )}
          </div>
        ) : (
          <span className="text-[10px] text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-[10px] text-muted-foreground whitespace-nowrap">
        {row.mapped ? fmtRelative(row.lastSyncedAt) : "—"}
      </td>
      <td className="px-3 py-2 text-right">
        <MapControl row={row} />
      </td>
    </tr>
  );
}

function MapControl({ row }: { row: SkyboxEventMapping }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const queryClient = useQueryClient();

  const { data: candidates, isFetching } = useListSkyboxEventCandidates(
    { fveEventId: row.fveEventId },
    {
      query: {
        enabled: open,
        queryKey: getListSkyboxEventCandidatesQueryKey({
          fveEventId: row.fveEventId,
        }),
      },
    },
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetSkyboxSummaryQueryKey() });
    queryClient.invalidateQueries({ queryKey: getListSkyboxEventsQueryKey() });
    queryClient.invalidateQueries({ queryKey: getListSkyboxExposureQueryKey() });
  };

  const mapMutation = useMapSkyboxEvent({
    mutation: {
      onSuccess: () => {
        invalidate();
        setOpen(false);
        toast.success("Mapping updated");
      },
      onError: () => toast.error("Failed to update mapping"),
    },
  });

  const filtered = useMemo(() => {
    const list = candidates ?? [];
    const needle = search.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((c) =>
      [c.name, c.venue, c.city, c.skyboxEventId].some((v) =>
        (v ?? "").toString().toLowerCase().includes(needle),
      ),
    );
  }, [candidates, search]);

  return (
    <div className="inline-flex items-center gap-1.5">
      {row.mapped && row.skyboxEventId && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1 text-[11px] text-muted-foreground hover:text-red-400"
          disabled={mapMutation.isPending}
          onClick={() =>
            mapMutation.mutate({
              skyboxEventId: row.skyboxEventId!,
              data: { fveEventId: null },
            })
          }
        >
          <Link2Off className="h-3.5 w-3.5" />
          Unmap
        </Button>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1 text-[11px]"
            disabled={mapMutation.isPending}
          >
            {row.mapped ? (
              <ChevronsUpDown className="h-3.5 w-3.5" />
            ) : (
              <Link2 className="h-3.5 w-3.5" />
            )}
            {row.mapped ? "Change" : "Map"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="end">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search Skybox events…"
              value={search}
              onValueChange={setSearch}
              className="h-8 text-xs"
            />
            <CommandList className="max-h-72">
              {isFetching ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  <CommandEmpty className="py-6 text-center text-xs text-muted-foreground">
                    No Skybox events found.
                  </CommandEmpty>
                  <CommandGroup>
                    {filtered.map((c) => {
                      const isCurrent = c.skyboxEventId === row.skyboxEventId;
                      const mappedElsewhere =
                        c.alreadyMappedTo != null &&
                        c.alreadyMappedTo !== row.fveEventId;
                      return (
                        <CommandItem
                          key={c.skyboxEventId}
                          value={c.skyboxEventId}
                          className="text-xs items-start"
                          onSelect={() => {
                            if (isCurrent) {
                              setOpen(false);
                              return;
                            }
                            mapMutation.mutate({
                              skyboxEventId: c.skyboxEventId,
                              data: {
                                fveEventId: row.fveEventId,
                                name: c.name,
                                date: c.date,
                                venue: c.venue,
                                city: c.city,
                                country: c.country,
                              },
                            });
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 mt-0.5 h-3.5 w-3.5 shrink-0",
                              isCurrent ? "opacity-100" : "opacity-0",
                            )}
                          />
                          <div className="min-w-0">
                            <div className="truncate text-foreground">
                              {c.name ?? c.skyboxEventId}
                            </div>
                            <div className="text-[10px] text-muted-foreground truncate">
                              {fmtDate(c.date)}
                              {c.venue ? ` · ${c.venue}` : ""}
                              {c.city ? ` · ${c.city}` : ""}
                            </div>
                            {mappedElsewhere && (
                              <div className="text-[10px] text-amber-400">
                                Mapped to {c.alreadyMappedLabel ?? "another event"}
                              </div>
                            )}
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function AlertsTab({
  alertConfigured,
  alertEnabled,
  alertChannel,
}: {
  alertConfigured: boolean;
  alertEnabled: boolean;
  alertChannel: string | null;
}) {
  const queryClient = useQueryClient();
  const { data: channels, isFetching: channelsFetching } = useListSlackChannels();
  const [enabled, setEnabled] = useState(alertEnabled);
  const [channel, setChannel] = useState<string | null>(alertChannel);
  const [pickerOpen, setPickerOpen] = useState(false);

  const saveMutation = useUpdateSkyboxAlertConfig({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetSkyboxSummaryQueryKey(),
        });
        toast.success("Alert settings saved");
      },
      onError: () => toast.error("Failed to save alert settings"),
    },
  });

  const channelName = useMemo(() => {
    if (!channel) return null;
    const found = (channels ?? []).find((c) => c.id === channel);
    return found?.name ?? channel;
  }, [channel, channels]);

  const dirty = enabled !== alertEnabled || channel !== alertChannel;

  return (
    <div className="p-4 max-w-xl space-y-5 overflow-auto custom-scrollbar h-full">
      {!alertConfigured && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
          Slack is not connected. Configure the Slack integration in Settings
          before enabling alerts.
        </div>
      )}

      <div className="flex items-start justify-between gap-4">
        <div>
          <Label className="text-sm text-foreground">
            Double-exposure alerts
          </Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            Post a Slack message when a new double-exposure flag is detected
            during a sync.
          </p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={setEnabled}
          disabled={!alertConfigured}
        />
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Channel</Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 gap-1 px-1.5 text-[11px] text-muted-foreground"
            disabled={!alertConfigured || channelsFetching}
            onClick={() => {
              queryClient.invalidateQueries({
                queryKey: getListSlackChannelsQueryKey(),
              });
            }}
          >
            <RefreshCw
              className={cn("h-3 w-3", channelsFetching && "animate-spin")}
            />
            Refresh
          </Button>
        </div>
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              role="combobox"
              aria-expanded={pickerOpen}
              disabled={!alertConfigured}
              className="h-8 w-72 text-xs bg-muted/50 inline-flex items-center justify-between gap-1 rounded-md border border-input px-3 py-1 text-foreground shadow-sm transition-colors hover:bg-muted focus:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
            >
              <span className="truncate">
                {channelName ? `#${channelName}` : "Select a channel…"}
              </span>
              <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="start">
            <Command>
              <CommandInput
                placeholder="Search channels…"
                className="h-8 text-xs"
              />
              <CommandList className="max-h-64">
                <CommandEmpty className="py-4 text-center text-xs text-muted-foreground">
                  No channels found.
                </CommandEmpty>
                <CommandGroup>
                  {(channels ?? []).map((c) => (
                    <CommandItem
                      key={c.id}
                      value={c.name}
                      className="text-xs"
                      onSelect={() => {
                        setChannel(c.id);
                        setPickerOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-3.5 w-3.5",
                          channel === c.id ? "opacity-100" : "opacity-0",
                        )}
                      />
                      #{c.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          className="h-8"
          disabled={
            !alertConfigured ||
            !dirty ||
            saveMutation.isPending ||
            (enabled && !channel)
          }
          onClick={() =>
            saveMutation.mutate({ data: { enabled, targetId: channel } })
          }
        >
          {saveMutation.isPending && (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          )}
          Save
        </Button>
        {enabled && !channel && (
          <span className="text-[11px] text-amber-400">
            Select a channel to enable alerts.
          </span>
        )}
      </div>
    </div>
  );
}
